
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Search, ArrowLeft, Target, CheckCircle,
  Loader2, AlertCircle, Eye, FileDown,
} from 'lucide-react';
import { coursePerformanceService } from '@/services/course-performance.service';
import type { CourseUsersListParams } from '@/types/course-performance.types';
import toast from 'react-hot-toast';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Pagination from '@/components/ui/Pagination';
import { TableSkeleton, MetricCardSkeleton } from '@/components/ui/SkeletonLoader';

// Helper function to format time from seconds to HH:MM:SS
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const STATUS_OPTIONS = [
  { label: 'All Status', value: 'all' },
  { label: 'Not Started', value: 'NOT_STARTED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Expired', value: 'EXPIRED' },
];

export default function CourseUsers() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [exportLoading, setExportLoading] = useState<{ excel: boolean; csv: boolean }>({ excel: false, csv: false });
  const location = useLocation();

  const queryParams: CourseUsersListParams = {
    page,
    limit,
    search,
    ...(filterStatus !== 'all' && { status: filterStatus })
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const { data: apiResponse, isLoading, error } = useQuery({
    queryKey: ['courseUsers', courseId, queryParams],
    queryFn: () => coursePerformanceService.getCourseUsers(courseId!, queryParams),
    enabled: !!courseId,
  });

  // Fetch analytics from API instead of calculating on client side
  const { data: analyticsResponse, isLoading: isAnalyticsLoading } = useQuery({
    queryKey: ['courseAnalytics', courseId],
    queryFn: () => coursePerformanceService.getCourseUserAnalytics(courseId!),
    enabled: !!courseId,
  });

  const userData = apiResponse?.data;
  const users = userData?.users ?? [];
  const pagination = userData?.pagination;
  const total_pages = pagination ? Math.ceil(pagination.total / limit) : 1;

  const handleExport = async (format: 'csv' | 'excel') => {
    if (!courseId) return;

    setExportLoading(prev => ({ ...prev, [format]: true }));

    try {
      const result = await coursePerformanceService.exportCourseUserData(courseId, format, {
        search: search || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        courseName: userData?.course_name || undefined,
      });

      if (result.success) {
        toast.success(`Exported ${result.count} users to ${format === 'excel' ? 'Excel' : 'CSV'}`);
      } else {
        toast.error(result.message || 'No data to export');
      }
    } catch (err: any) {
      console.error('Export failed:', err);
      toast.error(err.message || 'Failed to export user data.');
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // UI Helper function for status badges
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'not_started':
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Use API-calculated metrics
  const summary = React.useMemo(() => {
    if (!analyticsResponse) {
      return { total: 0, completed: 0, avgProgress: 0 };
    }

    return {
      total: analyticsResponse.total_assigned_users,
      completed: analyticsResponse.completed_count,
      avgProgress: Math.round(analyticsResponse.average_progress),
    };
  }, [analyticsResponse]);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={handleBackNavigation} className="p-2 rounded-full hover:bg-gray-100" title="Go back">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Course User Analytics</h1>
            <p className="text-gray-500">Showing enrolled users for Course: {userData?.course_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('excel')}
            disabled={exportLoading.excel || users.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {exportLoading.excel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Excel
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={exportLoading.csv || users.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {exportLoading.csv ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {isAnalyticsLoading ? (
        <MetricCardSkeleton count={3} className="grid-cols-1 md:grid-cols-3" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <Users className="w-8 h-8 text-brand-indigo-600 mb-2" />
            <p className="text-2xl font-bold">{summary.total}</p>
            <p className="text-sm text-gray-600">Total Users</p>
          </div>
          <div className="card">
            <CheckCircle className="w-8 h-8 text-brand-green-600 mb-2" />
            <p className="text-2xl font-bold">{summary.completed}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div className="card">
            <Target className="w-8 h-8 text-brand-blue-600 mb-2" />
            <p className="text-2xl font-bold">{summary.avgProgress}%</p>
            <p className="text-sm text-gray-600">Average Progress</p>
          </div>
        </div>
      )}

      {/* Filters & Table */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b flex items-center gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500"
            />
          </div>
          <div className="w-48">
            <SimpleSelect
              options={STATUS_OPTIONS}
              value={filterStatus}
              onChange={(val) => { setFilterStatus((val as string) || 'all'); setPage(1); }}
              placeholder="All Status"
              isSearchable={false}
            />
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={5} columns={5} showHeader={false} />
        ) : error ? (
          <div className="text-center p-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load user data.</p>
          </div>
        ) : (
          <>
            {users.length === 0 ? (
              <div className="text-center p-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-800">No Users Found</h3>
                <p className="text-gray-500 mt-1">
                  {search
                    ? `No users found matching your search for "${search}".`
                    : 'No users are enrolled in this course yet.'}
                </p>
              </div>
            ): (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progress</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          <div>Time Spent</div>
                          <div className="text-[10px] text-gray-400 normal-case mt-0.5">(HH:MM:SS)</div>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                     {users.map((user) => (
                        <tr key={user.user_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <p className="font-medium text-gray-900">{`${user.given_name} ${user.family_name}`}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(user.status)}`}>
                              {user.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-medium text-gray-900">{user.progress_percentage}%</span>
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div className="bg-brand-indigo-500 h-1.5 rounded-full" style={{ width: `${user.progress_percentage}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center text-sm">{formatTime(user.total_time_spent_seconds)}</td>
                          <td className="px-4 py-4 text-right">
                            <button
                              onClick={() => {
                                navigate(
                                  `/admin/course/assignments/user/${user.user_id}/course/${courseId}/progress`,
                                  { state: { from: location.pathname } }
                                );
                              }}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="View User Progress Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination && pagination.total > 0 && (
                  <Pagination
                    currentPage={page}
                    totalPages={total_pages}
                    totalItems={pagination.total}
                    itemsPerPage={limit}
                    onPageChange={(p) => setPage(p)}
                    onItemsPerPageChange={(val) => { setLimit(val); setPage(1); }}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50, 100]}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
